import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import AuthShell from '@/components/layout/AuthShell';
import AuthSplit, {
  AuthField,
  AuthInput,
  PasswordInput,
  AuthSubmit,
  FormAlert,
  formatApiError,
  describedBy,
} from '@/components/layout/AuthSplit';

// Illustrative rows for the brief panel (the card title says "Sample").
const LOGIN_BRIEF_ROWS = [
  { sev: 'critical', name: 'Ransomware affiliate · initial access', meta: 'Finance · North America', serial: 'SAMPLE-1' },
  { sev: 'high',     name: 'Credential phishing · SaaS tenants',    meta: 'Technology · Europe',     serial: 'SAMPLE-2' },
  { sev: 'high',     name: 'Exploited VPN appliance CVE',           meta: 'All sectors · KEV listed', serial: 'SAMPLE-3' },
  { sev: 'medium',   name: 'MFA fatigue campaign',                  meta: 'Healthcare · Global',     serial: 'SAMPLE-4' },
];

const CODE_LENGTH = 6;

/** "analyst@acme.com" -> "a••••••@acme.com", so the MFA step names the account. */
function maskEmail(email) {
  const [user, domain] = String(email).split('@');
  if (!domain) return email;
  return `${user.slice(0, 1)}${'•'.repeat(Math.max(user.length - 1, 2))}@${domain}`;
}

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState('credentials'); // 'credentials' | 'mfa'
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [badCredentials, setBadCredentials] = useState(false);
  const codeRef = useRef(null);
  const emailRef = useRef(null);

  const finish = () => {
    toast.success('Signed in');
    navigate('/home');
  };

  const submitCredentials = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError('');
    setBadCredentials(false);
    try {
      const result = await login(formData.email.trim(), formData.password);
      if (result.mfa_required) {
        setMfaCode('');
        setStep('mfa');
      } else {
        finish();
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setBadCredentials(true);
        setError('The email or password is incorrect.');
      } else {
        setError(formatApiError(err, 'Sign-in failed. Try again.').message);
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (code) => {
    if (loading) return;
    if (code.length !== CODE_LENGTH) {
      setError(`Enter the ${CODE_LENGTH}-digit code from your authenticator app.`);
      codeRef.current?.focus();
      return;
    }
    setLoading(true);
    setError('');
    try {
      await login(formData.email.trim(), formData.password, code);
      finish();
    } catch (err) {
      if (err.response?.status === 401) {
        setError('That code did not work. Codes change every 30 seconds; enter the current one.');
        setMfaCode('');
      } else {
        setError(formatApiError(err, 'Verification failed. Try again.').message);
      }
      setLoading(false);
      // Wait for the input to re-enable before focusing it.
      setTimeout(() => codeRef.current?.focus(), 0);
    }
  };

  const onCodeChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setMfaCode(digits);
    if (error) setError('');
    // Auto-submit once the full code is typed or pasted.
    if (digits.length === CODE_LENGTH) verifyCode(digits);
  };

  const switchAccount = () => {
    setStep('credentials');
    setMfaCode('');
    setError('');
    setFormData((f) => ({ ...f, password: '' }));
  };

  // Move focus with the step; on first load the page keeps its normal order.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (step === 'mfa') codeRef.current?.focus();
    else emailRef.current?.focus();
  }, [step]);

  return (
    <AuthShell variant="split">
      <AuthSplit>
        <AuthSplit.Brief
          sectionLabel="Morning Brief"
          headline="The adversaries your sector is fighting,"
          emphasis="in one brief."
          briefTitle="Active campaigns · Sample"
          rows={LOGIN_BRIEF_ROWS}
          quote="Every brief is matched to your industry, region and security stack, with duplicate coverage merged into one story."
        />
        <AuthSplit.Panel backTo="/">
          {step === 'credentials' ? (
            <>
              <div className="auth-head">
                <div className="auth-eyebrow">Sign in</div>
                <h1 className="auth-title">
                  Sign in to your <em>workspace.</em>
                </h1>
                <p className="auth-intro">
                  Your briefs, CVE coverage and detection rules, scoped to your organization.
                </p>
              </div>

              <form onSubmit={submitCredentials} className="auth-form" aria-describedby={error ? 'login-error' : undefined}>
                <FormAlert id="login-error" message={error} />

                <AuthField id="email" label="Work email">
                  <AuthInput
                    ref={emailRef}
                    id="email"
                    name="email"
                    data-testid="login-email-input"
                    type="email"
                    inputMode="email"
                    autoComplete="username"
                    autoCapitalize="off"
                    spellCheck={false}
                    placeholder="analyst@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    invalid={badCredentials}
                    aria-describedby={badCredentials ? 'login-error' : undefined}
                    required
                  />
                </AuthField>

                <AuthField id="password" label="Password">
                  <PasswordInput
                    id="password"
                    name="password"
                    data-testid="login-password-input"
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    invalid={badCredentials}
                    aria-describedby={badCredentials ? 'login-error' : undefined}
                    required
                  />
                </AuthField>

                <AuthSubmit data-testid="login-submit-btn" loading={loading} loadingLabel="Signing in…">
                  Sign in
                  <ArrowRight aria-hidden="true" />
                </AuthSubmit>
              </form>

              <div className="auth-switch">
                <span>Need access?</span>
                <Link to="/register" data-testid="register-link" className="auth-link">
                  How access works <ArrowRight size={12} aria-hidden="true" />
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="auth-head">
                <div className="auth-eyebrow">Step 2 of 2 · Verification</div>
                <h1 className="auth-title">
                  Two-factor <em>verification.</em>
                </h1>
                <p className="auth-intro">
                  Enter the {CODE_LENGTH}-digit code from the authenticator app linked to{' '}
                  <strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{maskEmail(formData.email)}</strong>.
                </p>
              </div>

              <form
                onSubmit={(e) => { e.preventDefault(); verifyCode(mfaCode); }}
                className="auth-form"
              >
                <FormAlert id="mfa-error" message={error} />

                <AuthField id="mfa_code" label="Authentication code" hint="The code refreshes every 30 seconds.">
                  <AuthInput
                    ref={codeRef}
                    id="mfa_code"
                    name="mfa_code"
                    data-testid="login-mfa-input"
                    className="auth-input--code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={CODE_LENGTH}
                    placeholder="000000"
                    value={mfaCode}
                    onChange={onCodeChange}
                    disabled={loading}
                    invalid={Boolean(error)}
                    aria-describedby={[describedBy('mfa_code', { hint: true }), error && 'mfa-error'].filter(Boolean).join(' ')}
                    required
                  />
                </AuthField>

                <AuthSubmit data-testid="login-submit-btn" loading={loading} loadingLabel="Verifying…">
                  <ShieldCheck aria-hidden="true" />
                  Verify and sign in
                </AuthSubmit>
              </form>

              <div className="auth-switch">
                <button type="button" className="auth-link auth-link-btn" onClick={switchAccount} data-testid="login-different-account">
                  Use a different account
                </button>
              </div>
            </>
          )}
        </AuthSplit.Panel>
      </AuthSplit>
    </AuthShell>
  );
};

export default Login;
